import { replayEmbedUrl } from "../replay/source";
import { fallbackOf } from "../settings/catalog";
import { useStructuralSetting, useText } from "../settings/context";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * ═══ LE REPLAY ═══
 *
 * Une page du site, après l'événement : ce qui a été filmé, à revoir.
 *
 * ⚠️ UNE VIDÉO, PAS UNE LISTE. Le prototype dessine une liste de séances filmées
 * (`data-p="replay"`, des `.card` dans une `.clist`), mais rien dans le produit ne la
 * porte : `EventConference` n'a ni adresse de vidéo ni durée, et la V1 ne sert qu'UNE
 * adresse Vimeo sous un message HTML (`screens/Replay.jsx`). Une liste engendrée à
 * partir de rien serait un écran de démonstration. On reprend donc exactement ce que
 * la V1 fait, avec le titre et le sous-titre que le prototype ajoute.
 *
 * ⚠️ LA VIDÉO SE LIT HORS DE LA CASCADE DES LANGUES, comme celle du direct : une
 * adresse est la même vidéo en six langues, mais elle est ENREGISTRÉE dans la ligne
 * de la langue où l'organisateur se trouvait. Voir `useStructuralSetting`. Le message
 * au-dessus, lui, est de la prose : il suit la cascade.
 *
 * ⚠️ SES RÉGLAGES V2, ET RIEN D'AUTRE. Il lisait aussi, en silence, les deux jeux de
 * clés V1 (`FO_MEETING_REPLAY_*`, `replay*`) quand les siens étaient vides : le panneau
 * montrait des champs vides et la page un message et une vidéo. Retiré le 2026-09-26,
 * comme pour la vidéo du direct. Une valeur V1 n'entre que par la reprise, qui l'écrit
 * dans le réglage V2, sous les yeux de l'organisateur.
 */
export function Replay() {
  const title = useText(
    "FO_MEETING_V2_REP_TITLE",
    fallbackOf("FO_MEETING_V2_REP_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_REP_SUBTITLE",
    fallbackOf("FO_MEETING_V2_REP_SUBTITLE"),
  );
  const empty = useText(
    "FO_MEETING_V2_REP_EMPTY",
    fallbackOf("FO_MEETING_V2_REP_EMPTY"),
  );

  const html = useText("FO_MEETING_V2_REP_HTML");

  const address = useStructuralSetting("FO_MEETING_V2_REP_URL");
  const url = typeof address === "string" ? replayEmbedUrl(address) : null;

  return (
    <section className="page">
      <div className="ttl" data-cfg="repTitle">
        <InPlace settingKey="FO_MEETING_V2_REP_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_REP_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Assaini comme tout HTML d'organisateur, voir `ui/Prose`. Les `iframe` de
          vidéo y survivent : un organisateur qui colle son lecteur DANS le message
          plutôt que dans le réglage dédié obtient le même résultat. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="repHtml" />
      )}

      {/* ⚠️ RIEN N'EST CHARGÉ TANT QUE L'ADRESSE MANQUE. Un cadre vers `undefined`
          affiche l'erreur de l'hébergeur en plein milieu du site ; et une adresse qui
          n'est pas d'insertion donne un cadre noir. Dans les deux cas on dit qu'il
          n'y a pas encore de replay, ce qui est vrai pour le participant. */}
      {url ? (
        <div className="replay__v" data-cfg="repVideo">
          <iframe
            className="replay__f"
            src={url}
            title={title}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <p className="empty" data-cfg="repVideo">
          {empty}
        </p>
      )}
    </section>
  );
}
